import { useEffect, useState } from 'react';

import { LoadingSpinner } from '@/components/shared/LoadingSpinner';
import { useToast } from '@/components/shared/Toast';
import { useGetOrgProjectsQuery } from '@/features/orgs/api/orgApi';
import { docId } from '@/features/agents/utils';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useUpdateNumberMutation } from '../api/numbersApi';
import { numbersErrorMessage } from '../utils';
import type { PhoneNumberView } from '../types';

const NO_PROJECT = 'none';

export interface EditNumberDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  orgId: string;
  number: PhoneNumberView;
  /** Resolved bound-agent name for the read-only context line (§8). */
  agentName?: string;
}

/**
 * Edit-number dialog (design §8). `PATCH /v1/voice-bridge/numbers/:id` with
 * `label`/`projectId` only — `factoryId` is not patchable (API fact),
 * `apiKeyId` is display-only (audit #10) and `isActive` is owned by the
 * StatusToggle column (H3). "No project" sends `projectId: null` to clear the
 * association (OQ-8).
 */
export function EditNumberDialog({
  open,
  onOpenChange,
  orgId,
  number,
  agentName,
}: EditNumberDialogProps) {
  const toast = useToast();
  const [updateNumber, { isLoading }] = useUpdateNumberMutation();
  const { data: projects } = useGetOrgProjectsQuery(orgId);

  const [label, setLabel] = useState(number.label ?? '');
  const [projectId, setProjectId] = useState<string>(number.projectId ?? NO_PROJECT);

  // Prefill from the row each time the dialog opens.
  useEffect(() => {
    if (open) {
      setLabel(number.label ?? '');
      setProjectId(number.projectId ?? NO_PROJECT);
    }
  }, [open, number]);

  async function handleSubmit() {
    try {
      await updateNumber({
        id: number.id,
        changes: {
          label: label.trim(),
          projectId: projectId === NO_PROJECT ? null : projectId,
        },
      }).unwrap();
      toast.success('Number updated.');
      onOpenChange(false);
    } catch (error) {
      toast.error(numbersErrorMessage(error, "Couldn't update number."));
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!isLoading) {
          onOpenChange(next);
        }
      }}
    >
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Edit number</DialogTitle>
          <DialogDescription className="sr-only">
            Update the label and project of {number.number}.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1">
            <p className="font-mono text-sm">{number.number}</p>
            {number.factoryId ? (
              <p className="text-sm text-muted-foreground">
                Bound to {agentName ?? number.factoryId}.
              </p>
            ) : null}
          </div>

          <div className="space-y-2">
            <Label htmlFor="edit-label">Label (optional)</Label>
            <Input
              id="edit-label"
              value={label}
              onChange={(event) => setLabel(event.target.value)}
              placeholder="e.g. Support line"
              disabled={isLoading}
            />
            <p className="text-sm text-muted-foreground">
              A short name shown in lists and call logs.
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="edit-project">Project (optional)</Label>
            <Select value={projectId} onValueChange={setProjectId} disabled={isLoading}>
              <SelectTrigger id="edit-project">
                <SelectValue placeholder="No project" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NO_PROJECT}>No project</SelectItem>
                {(projects ?? [])
                  .filter((project) => project.isActive !== false)
                  .map((project) => {
                    const id = docId(project);
                    return id ? (
                      <SelectItem key={id} value={id}>
                        {project.name ?? id}
                      </SelectItem>
                    ) : null;
                  })}
              </SelectContent>
            </Select>
            <p className="text-sm text-muted-foreground">Projects in this organization.</p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isLoading}>
            Cancel
          </Button>
          <Button onClick={() => void handleSubmit()} disabled={isLoading}>
            {isLoading ? <LoadingSpinner size="sm" className="mr-2" label="Saving" /> : null}
            {isLoading ? 'Saving…' : 'Save changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
